/**
 * Modal to confirm linking the chosen task (second step of link flow).
 *
 * @module linkTaskConfirmModal
 */
import { Tags } from "@risalabs_frontend_org/oasis-ui-kit";
import { useEffect, type ReactNode } from "react";
import { closeModal, Modal, openModal } from "risa-oasis-ui_v2";
import { ModalId } from "../../../../../enums/modalId";
import type { LinkTaskCandidate } from "./linkTaskTypes";
import { getStatusTagColors } from "./tasksConversationPanel";

/**
 * Props for {@link LinkTaskConfirmModal}.
 */
interface LinkTaskConfirmModalProps {
  onClose: () => void;
  task: LinkTaskCandidate;
  onLink?: (task: LinkTaskCandidate) => void;
}

/**
 * One dashed divider row: bold label on the left, value centered in the remaining width.
 *
 * @param props.label - Left column label text (a colon is appended in the UI).
 * @param props.children - Right column content (text, tag, etc.).
 * @returns Row markup shared by task summary fields.
 */
function LinkConfirmDetailRow({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-4 border-b border-dashed border-primaryGray-14 py-2.5">
      <span className="text-tiny font-bold leading-[1.125rem] tracking-[0.0125rem] text-primaryGray-2">
        {label}:
      </span>
      <div className="flex min-w-0 items-center justify-center text-center">
        {children}
      </div>
    </div>
  );
}

/**
 * Right-column plain text styling for {@link LinkConfirmDetailRow}.
 *
 * @param props.text - Value string to display.
 * @returns Muted centered summary text.
 */
function LinkConfirmTextValue({ text }: { text: string }) {
  return (
    <span className="max-w-full break-words text-tiny font-normal leading-[1.125rem] tracking-[0.0125rem] text-primaryGray-2">
      {text}
    </span>
  );
}

/**
 * Shows task summary and Link / Cancel; completes the link flow on confirm.
 *
 * @param props - Task to summarize, close handler, and optional link callback.
 * @returns Oasis modal for link confirmation.
 */
const LinkTaskConfirmModal = ({
  onClose,
  task,
  onLink,
}: LinkTaskConfirmModalProps) => {
  useEffect(() => {
    openModal(ModalId.LINK_EXISTING_TASK_CONFIRM_MODAL);
  }, []);

  const dismiss = () => {
    closeModal(ModalId.LINK_EXISTING_TASK_CONFIRM_MODAL, onClose);
  };

  const handleLink = () => {
    onLink?.(task);
    dismiss();
  };

  const statusColors = getStatusTagColors(task.statusVariant ?? "open");

  return (
    <Modal
      dialogId={ModalId.LINK_EXISTING_TASK_CONFIRM_MODAL}
      title="Are you sure you want to link this task?"
      saveButtonText="Link"
      cancelText="Cancel"
      onSave={handleLink}
      onCancel={dismiss}
      onClose={dismiss}
      disableSave={false}
    >
      <div className="flex w-full flex-col gap-4 pb-2 pt-1">
        <div className="flex flex-col">
          <LinkConfirmDetailRow label="Task">
            <LinkConfirmTextValue text={task.title} />
          </LinkConfirmDetailRow>
          <LinkConfirmDetailRow label="Status">
            <Tags
              label={task.status}
              backgroundColor={statusColors.backgroundColor}
              textColor={statusColors.textColor}
              className="!m-0 shrink-0"
            />
          </LinkConfirmDetailRow>
          <LinkConfirmDetailRow label="Last Updated">
            <LinkConfirmTextValue text={task.lastUpdated} />
          </LinkConfirmDetailRow>
          <LinkConfirmDetailRow label="Assignee">
            <LinkConfirmTextValue text={task.assignee} />
          </LinkConfirmDetailRow>
        </div>
        <div className="flex flex-col gap-2">
          <span className="text-tiny font-bold tracking-[0.0125rem] text-primaryGray-2">
            Latest Note:
          </span>
          <div className="rounded bg-primaryGray-16 px-3 py-2">
            <p className="text-tiny font-normal leading-[1.25rem] text-primaryGray-2">
              {task.latestNote}
            </p>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default LinkTaskConfirmModal;
